import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { authAPI } from '../services/api';

const Profile = () => {
  const navigate = useNavigate();
  const { user, checkAuth, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    phone: '',
    profile_picture: ''
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [previewImage, setPreviewImage] = useState(null);
  const [otpEnabled, setOtpEnabled] = useState(user?.otp_enabled === 1);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    
    setProfileData({
      full_name: user.full_name || '',
      email: user.email || '',
      phone: user.phone || '',
      profile_picture: user.profile_picture || ''
    });
    setPreviewImage(user.profile_picture || null);
    setOtpEnabled(user.otp_enabled === 1);
  }, [user, navigate]);

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
        setProfileData({
          ...profileData,
          profile_picture: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await authAPI.updateProfile(profileData);
      setSuccess('Profile updated successfully!');
      await checkAuth(); // Refresh user data
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPToggle = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await authAPI.toggleOTP({ otp_enabled: !otpEnabled });
      setOtpEnabled(!otpEnabled);
      setSuccess(`OTP verification ${!otpEnabled ? 'enabled' : 'disabled'} successfully!`);
      await checkAuth(); // Refresh user data
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update OTP settings');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (passwordData.new_password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      await authAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      setSuccess('Password changed successfully!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return;
    }

    try {
      await authAPI.deleteAccount();
      logout();
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete account');
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Header />
      <main>
        <section className="section">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h1 style={{ 
              textAlign: 'center', 
              color: 'var(--maroon)', 
              fontFamily: "'Playfair Display',serif", 
              fontSize: '36px', 
              marginBottom: '10px' 
            }}>
              My Profile
            </h1>
            <p style={{ textAlign: 'center', color: 'var(--muted)', marginBottom: '30px' }}>
              Manage your account settings and preferences
            </p>

            {/* Tabs */}
            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              marginBottom: '30px', 
              borderBottom: '2px solid rgba(122,19,43,0.1)',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => setActiveTab('profile')}
                style={{
                  padding: '12px 24px',
                  background: activeTab === 'profile' ? 'var(--maroon)' : 'transparent',
                  color: activeTab === 'profile' ? '#fff' : 'var(--maroon)',
                  border: 'none',
                  borderRadius: '8px 8px 0 0',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
              >
                Profile Settings
              </button>
              <button
                onClick={() => setActiveTab('password')}
                style={{
                  padding: '12px 24px',
                  background: activeTab === 'password' ? 'var(--maroon)' : 'transparent',
                  color: activeTab === 'password' ? '#fff' : 'var(--maroon)',
                  border: 'none',
                  borderRadius: '8px 8px 0 0',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
              >
                Change Password
              </button>
              <button
                onClick={() => setActiveTab('security')}
                style={{
                  padding: '12px 24px',
                  background: activeTab === 'security' ? 'var(--maroon)' : 'transparent',
                  color: activeTab === 'security' ? '#fff' : 'var(--maroon)',
                  border: 'none',
                  borderRadius: '8px 8px 0 0',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
              >
                Security
              </button>
              <button
                onClick={() => setActiveTab('danger')}
                style={{
                  padding: '12px 24px',
                  background: activeTab === 'danger' ? '#dc2626' : 'transparent',
                  color: activeTab === 'danger' ? '#fff' : '#dc2626',
                  border: 'none',
                  borderRadius: '8px 8px 0 0',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
              >
                Danger Zone
              </button>
            </div>

            {/* Success/Error Messages */}
            {error && (
              <div style={{
                padding: '12px',
                background: 'rgba(220, 38, 38, 0.1)',
                border: '1px solid rgba(220, 38, 38, 0.3)',
                borderRadius: '8px',
                color: '#dc2626',
                marginBottom: '20px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            {success && (
              <div style={{
                padding: '12px',
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '8px',
                color: '#16a34a',
                marginBottom: '20px',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                ✓ {success}
              </div>
            )}

            {/* Profile Settings Tab */}
            {activeTab === 'profile' && (
              <div className="card" style={{ padding: '40px' }}>
                <h2 style={{ color: 'var(--maroon)', marginBottom: '24px' }}>Profile Information</h2>
                
                <form onSubmit={handleProfileSubmit}>
                  {/* Profile Picture */}
                  <div style={{ marginBottom: '30px', textAlign: 'center' }}>
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{
                        width: '150px',
                        height: '150px',
                        borderRadius: '50%',
                        margin: '0 auto',
                        overflow: 'hidden',
                        border: '4px solid var(--maroon)',
                        background: '#f3f4f6'
                      }}>
                        {previewImage ? (
                          <img 
                            src={previewImage} 
                            alt="Profile" 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '48px',
                            color: 'var(--maroon)',
                            fontWeight: '700'
                          }}>
                            {user.full_name?.charAt(0)?.toUpperCase() || user.username?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                      </div>
                    </div>
                    <input
                      type="file"
                      id="profile_picture"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                    />
                    <label
                      htmlFor="profile_picture"
                      style={{
                        display: 'inline-block',
                        padding: '10px 20px',
                        background: 'var(--accent-grad)',
                        color: '#3b0b12',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Change Profile Picture
                    </label>
                    <p style={{ color: 'var(--muted)', fontSize: '12px', marginTop: '8px' }}>
                      JPG, PNG or GIF. Max size 5MB.
                    </p>
                  </div>

                  {/* Full Name */}
                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="full_name" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="full_name"
                      name="full_name"
                      value={profileData.full_name}
                      onChange={handleProfileChange}
                      required
                      style={{ width: '100%' }}
                    />
                  </div>

                  {/* Email */}
                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="email" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                      required
                      style={{ width: '100%' }}
                    />
                  </div>

                  {/* Phone */}
                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="phone" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={profileData.phone}
                      onChange={handleProfileChange}
                      placeholder="+91 1234567890"
                      style={{ width: '100%' }}
                    />
                  </div>

                  {/* Account Info */}
                  <div style={{
                    marginTop: '30px',
                    padding: '16px',
                    background: 'rgba(122,19,43,0.05)',
                    borderRadius: '8px',
                    marginBottom: '20px'
                  }}>
                    <p style={{ margin: '0 0 8px', fontSize: '14px', color: 'var(--muted)' }}>
                      <strong>Username:</strong> {user.username}
                    </p>
                    <p style={{ margin: '0 0 8px', fontSize: '14px', color: 'var(--muted)' }}>
                      <strong>Role:</strong> {user.role === 'admin' ? 'Administrator' : 'User'}
                    </p>
                    <p style={{ margin: '0', fontSize: '14px', color: 'var(--muted)' }}>
                      <strong>Member since:</strong> {new Date(user.created_at).toLocaleDateString()}
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: 'var(--accent-grad)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#3b0b12',
                      fontWeight: '800',
                      fontSize: '16px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? 'Updating...' : 'Update Profile'}
                  </button>
                </form>
              </div>
            )}

            {/* Change Password Tab */}
            {activeTab === 'password' && (
              <div className="card" style={{ padding: '40px' }}>
                <h2 style={{ color: 'var(--maroon)', marginBottom: '24px' }}>Change Password</h2>
                
                <form onSubmit={handlePasswordSubmit}>
                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="current_password" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Current Password
                    </label>
                    <input
                      type="password"
                      id="current_password"
                      name="current_password"
                      value={passwordData.current_password}
                      onChange={handlePasswordChange}
                      required
                      placeholder="••••••••"
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="new_password" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      New Password
                    </label>
                    <input
                      type="password"
                      id="new_password"
                      name="new_password"
                      value={passwordData.new_password}
                      onChange={handlePasswordChange}
                      required
                      minLength="6"
                      placeholder="••••••••"
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="confirm_password" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      id="confirm_password"
                      name="confirm_password"
                      value={passwordData.confirm_password}
                      onChange={handlePasswordChange}
                      required
                      placeholder="••••••••"
                      style={{ width: '100%' }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: 'var(--accent-grad)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#3b0b12',
                      fontWeight: '800',
                      fontSize: '16px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? 'Changing Password...' : 'Change Password'}
                  </button>
                </form>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="card" style={{ padding: '40px' }}>
                <h2 style={{ color: 'var(--maroon)', marginBottom: '24px' }}>Security Settings</h2>
                
                <div style={{
                  padding: '24px',
                  background: 'rgba(122,19,43,0.05)',
                  borderRadius: '12px',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ color: 'var(--maroon)', marginBottom: '8px', fontSize: '18px' }}>
                        OTP Verification on Sign In
                      </h3>
                      <p style={{ color: 'var(--muted)', fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' }}>
                        When enabled, you'll receive a one-time password (OTP) via email every time you sign in. 
                        This adds an extra layer of security to your account.
                      </p>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '13px',
                        color: otpEnabled ? '#16a34a' : '#dc2626',
                        fontWeight: '600'
                      }}>
                        {otpEnabled ? '✓ Currently Enabled' : '✗ Currently Disabled'}
                      </div>
                    </div>
                    <button
                      onClick={handleOTPToggle}
                      disabled={loading}
                      style={{
                        padding: '10px 20px',
                        background: otpEnabled ? '#dc2626' : '#16a34a',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff',
                        fontWeight: '700',
                        fontSize: '14px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.7 : 1,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {loading ? 'Updating...' : (otpEnabled ? 'Disable OTP' : 'Enable OTP')}
                    </button>
                  </div>

                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: 'rgba(255, 193, 7, 0.1)',
                    borderLeft: '4px solid #ffc107',
                    borderRadius: '4px'
                  }}>
                    <p style={{ margin: 0, fontSize: '13px', color: '#856404', lineHeight: '1.5' }}>
                      <strong>Note:</strong> OTP verification is recommended for enhanced account security. 
                      You can disable it anytime if you prefer faster sign-ins without OTP verification.
                    </p>
                  </div>
                </div>

                <div style={{
                  marginTop: '30px',
                  padding: '20px',
                  background: '#fff',
                  borderRadius: '12px',
                  border: '1px solid rgba(122,19,43,0.1)'
                }}>
                  <h3 style={{ color: 'var(--maroon)', marginBottom: '12px', fontSize: '16px' }}>
                    Security Tips
                  </h3>
                  <ul style={{ paddingLeft: '20px', margin: 0, color: 'var(--muted)', fontSize: '14px', lineHeight: '1.8' }}>
                    <li>Use a strong, unique password for your account</li>
                    <li>Enable OTP verification for additional security</li>
                    <li>Never share your password or OTP with anyone</li>
                    <li>Check your email for any suspicious sign-in attempts</li>
                    <li>Keep your email account secure as it's used for account recovery</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Danger Zone Tab */}
            {activeTab === 'danger' && (
              <div className="card" style={{ padding: '40px', border: '2px solid #dc2626' }}>
                <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>Danger Zone</h2>
                <p style={{ color: 'var(--muted)', marginBottom: '24px' }}>
                  Once you delete your account, there is no going back. Please be certain.
                </p>
                
                <button
                  onClick={handleDeleteAccount}
                  style={{
                    padding: '14px 24px',
                    background: '#dc2626',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontWeight: '800',
                    fontSize: '16px',
                    cursor: 'pointer'
                  }}
                >
                  Delete My Account
                </button>
              </div>
            )}
          </div>
        </section>
        <Footer />
      </main>
    </>
  );
};

export default Profile;
